/**
 * Calculate the distance between two geographic points using the Haversine formula.
 * @returns distance in meters
 */
export function calculateHaversineDistance(
  latA: number,
  lonA: number,
  latB: number,
  lonB: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const lat1Rad = (latA * Math.PI) / 180;
  const lat2Rad = (latB * Math.PI) / 180;
  const deltaLatRad = ((latB - latA) * Math.PI) / 180;
  const deltaLonRad = ((lonB - lonA) * Math.PI) / 180;

  const a =
    Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
    Math.cos(lat1Rad) *
      Math.cos(lat2Rad) *
      Math.sin(deltaLonRad / 2) *
      Math.sin(deltaLonRad / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Format distance in meters to a human-readable string.
 * Shows km with 1 decimal for distances >= 1000m, otherwise meters.
 */
export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
}

/**
 * Calculate the number of matching leading digits between two postal codes.
 * Higher = more similar area.
 */
export function postalCodeProximity(zipA: string, zipB: string): number {
  let matching = 0;
  const minLen = Math.min(zipA.length, zipB.length);
  for (let i = 0; i < minLen; i += 1) {
    if (zipA[i] === zipB[i]) {
      matching += 1;
    } else {
      break;
    }
  }
  return matching;
}
